<template>
  <Box :aspect="0.4" class="map-container">
    <GMapMap
      ref="map"
      :center="center"
      :zoom="bicycles?.length ? 14 : 10"
      :options="defaultOptions"
      class="h-fit"
      map-type-id="roadmap"
    >

      <GMapCircle
        v-if="visibilityLocation"
        key="locationKey"
        :radius="120"
        :center="props.visibilityLocation?.gisLocation ? {
          lat: visibilityLocation.gisLocation.lat,
          lng: visibilityLocation.gisLocation.long,
        } : defaultCenter"
        :options="{
          clickable: false,
          strokeWeight: 0,
          fillColor: '#000000',
          fillOpacity: 0.3,
          editable: false,
        }"
      />
      <GMapCircle
        v-for="(bicycle, index) in bicycles"
        :key="index"
        :radius="radius ?? 50"
        :center="bicycle.gisLocation ? {
          lat: bicycle.gisLocation.lat,
          lng: bicycle.gisLocation.long,
        } : defaultCenter"
        :options="{
          clickable: true,
          strokeWeight: 0,
          fillColor: bicycle.selected ? '#FF0000' : '#7B61FF',
          fillOpacity: 0.3,
          editable: false,
        }"
        @click="toggleSelection(index)"
      />
    </GMapMap>
  </Box>
</template>

<script lang="ts" setup>
import { computed, ref, watch } from 'vue';
import Box from '@components/utils/Box.vue';
import { syncRef } from '@hooks/state';
import type {
  VisibilityBicycleDetail,
  VisibilityLocation,
} from '@/entities/Entities';
import { defaultCenter, defaultOptions } from '@/utils/map';

interface Props {
  visibilityLocation: VisibilityLocation | null;
  visibilityBicycles?: VisibilityBicycleDetail[];
  radius?: number;
  readOnly?: boolean;
}

const props = defineProps<Props>();
const emit = defineEmits(['update:visibilityBicycles', 'update:radius']);

const bicycles = syncRef(props.visibilityBicycles);
const visibilityLocation = syncRef(props.visibilityLocation);

const radius = ref(props.radius ?? 100);
const center = ref({
  lat: props.visibilityBicycles?.find((b) => b?.gisLocation?.lat)?.gisLocation?.lat ?? 46.8000,
  lng: props.visibilityBicycles?.find((b) => b?.gisLocation?.long)?.gisLocation?.long ?? 8.2333,
});

const toggleSelection = (index: number) => {
  if (props.readOnly) {
    return;
  }

  const selectedBicycle = bicycles.value?.[index];

  if (selectedBicycle) {
    const { lat, long } = selectedBicycle.gisLocation ?? {};
    const selected = !selectedBicycle.selected;

    // Toggle the selected state for all bicycles with the same lat/long
    for (const bicycle of bicycles.value ?? []) {
      if (bicycle.gisLocation?.lat === lat && bicycle.gisLocation?.long === long) {
        bicycle.selected = selected; // Toggle selection
      }
    }

    // Emit the updated list
    emit('update:visibilityBicycles', [...(bicycles.value ?? [])]);
  }
};

watch(
  () => props.visibilityBicycles,
  (newBicycles) => {
    if (newBicycles) {
      bicycles.value = newBicycles.map((b) => ({
        ...b,
        selected: b.selected, // Ensure correct selection sync
      }));
    }
  },
  { deep: true, immediate: true },
);

watch(
  () => props.visibilityLocation,
  (newLoc) => {
    if (newLoc) {
      visibilityLocation.value = newLoc;
      center.value = {
        lat: newLoc?.gisLocation?.lat || defaultCenter.lat,
        lng: newLoc?.gisLocation?.long || defaultCenter.lng,
      };
    }
  },
  { deep: true, immediate: true },
);
</script>
